import React, { useState, useEffect, useRef } from 'react';
import { 
  Home, 
  Package, 
  Route, 
  ShoppingCart, 
  RotateCcw, 
  DollarSign, 
  MapPin, 
  Camera, 
  LogOut,
  TrendingUp,
  Users,
  Clock,
  Phone,
  Navigation,
  Map as MapIcon,
  Edit,
  CheckCircle2,
  ArrowUpDown
} from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { getDocuments, db, createOutletWithSerial } from '../services/firebase';
import { doc, setDoc, updateDoc, serverTimestamp, query, where, getDocs, collection, onSnapshot, writeBatch } from 'firebase/firestore';
import InventoryManagement from './InventoryManagement';
import WarehouseDashboard from './WarehouseDashboard';
import Sales from './Sales';
import AddOutlet from './AddOutlet';
// Marker clustering removed for Routes view simplicity
import { getFilteredOutletsForUser } from '../utils/outletFiltering';
import { RouteDailyStatus } from '../types/inventory';
// dnd-kit for mobile-friendly long-press drag & drop
import { DndContext, DragEndEvent, DragStartEvent, DragMoveEvent, PointerSensor, TouchSensor, useSensor, useSensors, closestCenter, MeasuringStrategy, DragOverlay } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UnifiedInventoryTransactionService } from '../services/unifiedInventoryTransactions';
import { useVehicleContext } from '../contexts/VehicleContext';

// Google Maps type declarations
declare global {
  interface Window {
    google: {
      maps: {
        Map: any;
        Marker: any;
        InfoWindow: any;
        SymbolPath: any;
        Polyline: any;
        DirectionsService: any;
        DirectionsRenderer: any;
        TravelMode: any;
      };
    };
  }
}

type ViewType = 'dashboard' | 'inventory' | 'sales' | 'routes' | 'outlets' | 'addOutlet';

const Dashboard: React.FC = () => {
  // Helper: get today's date as YYYY-MM-DD in IST reliably
  const getTodayDateStringIST = () => {
    const nowIST = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const yyyy = nowIST.getFullYear();
    const mm = String(nowIST.getMonth() + 1).padStart(2, '0');
    const dd = String(nowIST.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  const { currentUser, logout } = useAuth();
  const [currentView, setCurrentView] = useState<ViewType>('dashboard');
  const [activeTab, setActiveTab] = useState<'dashboard' | 'inventory' | 'routes' | 'outlets'>('dashboard');
  const [outlets, setOutlets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [editingOutlet, setEditingOutlet] = useState<any>(null);
  const [outletVisits, setOutletVisits] = useState<{ [outletId: string]: any }>({});
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const clusterRef = useRef<any>(null);
  const polylinesRef = useRef<any[]>([]);
  const directionsRendererRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [stats, setStats] = useState({
    totalSales: 0,
    totalReturns: 0,
    totalOrders: 0,
    totalCashCollected: 0,
    outletsVisited: 0,
    targetAchievement: 0
  });
  const [preselectedOutlet, setPreselectedOutlet] = useState<any | null>(null);
  const [routeStatus, setRouteStatus] = useState<RouteDailyStatus | null>(null);
  const [processingSettlement, setProcessingSettlement] = useState(false);
  const [confirmingOnField, setConfirmingOnField] = useState(false);
  // Edit sale mode
  const [editingSale, setEditingSale] = useState<{
    outlet: any;
    transactionId: string;
    salesDetails: any[];
    discount: number;
  } | null>(null);
  // Slot-aware Add New: store intended insertion sequence for new outlet
  const [insertAtSeq, setInsertAtSeq] = useState<number | null>(null);
  // Vehicle selection (moved from Beat Management → Dashboard Home)
  const {
    availableVehicles,
    assignedVehicleId,
    selectedVehicleId,
    setSelectedVehicleId,
    loading: vehiclesLoading,
  } = useVehicleContext();
  
  // Persist vehicle selection to today's route status
  const handleVehicleSelection = async (newVehicleId?: string) => {
    setSelectedVehicleId(newVehicleId);
    if (!currentUser || currentUser.role !== 'salesman') return;
    try {
      const today = getTodayDateStringIST();
      // Resolve salesman's route for today
      const beatsQueryRef = query(
        collection(db, 'beats'),
        where('salesmanId', '==', currentUser.id),
        where('forDate', '==', today),
        where('isActive', '==', true)
      );
      const beatsSnapshot = await getDocs(beatsQueryRef);
      if (beatsSnapshot.empty) return;
      const beat = beatsSnapshot.docs[0].data() as any;
      const assignedRouteId = beat.routeId as string | undefined;
      if (!assignedRouteId) return;

      // Find or create route_daily_status for today
      const rdsQuery = query(
        collection(db, 'route_daily_status'),
        where('routeId', '==', assignedRouteId),
        where('date', '==', today)
      );
      const rdsSnapshot = await getDocs(rdsQuery);
      let rdsRef: any;
      let currentStatus: string | undefined = undefined;
      if (!rdsSnapshot.empty) {
        rdsRef = rdsSnapshot.docs[0].ref;
        currentStatus = (rdsSnapshot.docs[0].data() as any).status;
      } else {
        rdsRef = doc(db, 'route_daily_status', `${assignedRouteId}_${today}`);
        await setDoc(rdsRef, {
          routeId: assignedRouteId,
          date: today,
          status: 'planning',
          createdAt: serverTimestamp(),
          createdBy: currentUser.id
        }, { merge: true } as any);
        currentStatus = 'planning';
      }

      // Lock edits once confirmed/on_field or beyond
      if (currentStatus && ['on_field','settling','settled'].includes(currentStatus)) {
        alert('Vehicle cannot be changed after loadout is confirmed.');
        return;
      }

      const v = availableVehicles.find(x => x.vehicleId === newVehicleId);
      await updateDoc(rdsRef, {
        vehicleId: newVehicleId || null,
        vehicleNumber: v?.vehicleNumber || null,
        lastUpdatedBy: currentUser.id,
        updatedAt: serverTimestamp()
      });
    } catch (e) {
      console.error('Failed to persist vehicle selection', e);
    }
  };
  // Slider state for route actions
  const [isDragging, setIsDragging] = useState(false);
  const [thumbPos, setThumbPos] = useState(0); // -100..100 (left..right)
  const dragStartPosRef = useRef<number | null>(null);
  const startThumbPosRef = useRef<number>(0);
  const trackRef = useRef<HTMLDivElement>(null);

  // Helper: compute first pending (unvisited) outlet sequence and id
  const getFirstPendingInfo = () => {
    // Assumption: outlets state is sorted by outletSequence asc when present
    let firstPendingOutlet: any | null = null;
    for (const o of outlets) {
      const oid = (o as any).id;
      if (!oid) continue;
      const visited = !!outletVisits[oid];
      if (!visited) {
        firstPendingOutlet = o;
        break;
      }
    }
    const firstPendingSeq = (firstPendingOutlet as any)?.outletSequence as number | undefined;
    const firstPendingId = (firstPendingOutlet as any)?.id as string | undefined;
    return {
      firstPendingSeq: typeof firstPendingSeq === 'number' ? firstPendingSeq : undefined,
      firstPendingId
    };
  };

  // dnd-kit sensors for mobile (long-press friendly pointer sensor with small activation distance)
  const sensors = useSensors(
    // Touch-specific sensor: require a deliberate long-press before drag
    useSensor(TouchSensor, { activationConstraint: { delay: 350, tolerance: 8 } }),
    // Pointer sensor for mouse/desktop
    useSensor(PointerSensor)
  );

  // Track current dragging item id for UX tweaks if needed
  const [draggingId, setDraggingId] = useState<string | null>(null);
  // Track active draggable id for DragOverlay
  const [activeId, setActiveId] = useState<string | null>(null);
  const dragStartClientYRef = useRef<number | null>(null);
  // Edge-triggered auto-scroll state
  const pointerClientYRef = useRef<number | null>(null);
  const autoScrollRafRef = useRef<number | null>(null);
  const autoScrollActiveRef = useRef<boolean>(false);

  const stopAutoScrollLoop = () => {
    autoScrollActiveRef.current = false;
    if (autoScrollRafRef.current != null) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
  };

  const autoScrollTick = () => {
    if (!autoScrollActiveRef.current) return;
    const y = pointerClientYRef.current;
    if (y == null) {
      autoScrollRafRef.current = requestAnimationFrame(autoScrollTick);
      return;
    }
    const vh = window.innerHeight || 0;
    const topZone = 20; // px (reduced per feedback)
    const bottomZone = 60; // px
    let dy = 0;
    if (y <= topZone) {
      const intensity = Math.min(20, Math.max(2, (topZone - y) / 4));
      dy = -intensity;
    } else if (y >= vh - bottomZone) {
      const over = y - (vh - bottomZone);
      const intensity = Math.min(20, Math.max(2, over / 4));
      dy = intensity;
    }
    if (dy !== 0) {
      window.scrollBy({ top: dy, left: 0, behavior: 'auto' });
      autoScrollRafRef.current = requestAnimationFrame(autoScrollTick);
    } else {
      // Immediately stop when retracted from the edge zones
      stopAutoScrollLoop();
    }
  };

  const ensureAutoScrollLoop = () => {
    if (!autoScrollActiveRef.current) {
      autoScrollActiveRef.current = true;
      autoScrollRafRef.current = requestAnimationFrame(autoScrollTick);
    }
  };

  const handleGlobalPointerMove = (clientY: number | null) => {
    pointerClientYRef.current = clientY;
    if (clientY == null) return;
    const vh = window.innerHeight || 0;
    const inTopZone = clientY <= 20;
    const inBottomZone = clientY >= vh - 60;
    if (inTopZone || inBottomZone) {
      ensureAutoScrollLoop();
    } else {
      stopAutoScrollLoop();
    }
  };

  const pointerMoveListener = (e: PointerEvent) => {
    handleGlobalPointerMove(typeof e.clientY === 'number' ? e.clientY : null);
  };
  const touchMoveListener = (e: TouchEvent) => {
    const y = e.touches && e.touches[0] ? e.touches[0].clientY : null;
    handleGlobalPointerMove(y);
  };

  // Sortable item component (pending-only)
  const [dragHintId, setDragHintId] = useState<string | null>(null);
  const isTouch = typeof window !== 'undefined' && ('ontouchstart' in window || (navigator as any)?.maxTouchPoints > 0);

  const SortableOutletItem: React.FC<{ outlet: any; firstPendingId?: string }> = ({ outlet, firstPendingId }) => {
    const id = (outlet as any).id as string;
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 100 : undefined,
      boxShadow: isDragging ? '0 8px 16px rgba(0,0,0,0.15)' : undefined,
      cursor: 'grab',
      opacity: isDragging ? 0 : 1 // keep space to allow siblings to yield smoothly
    } as React.CSSProperties;

    const visited = !!outletVisits[id];
    const bubbleClass = visited ? 'bg-green-600' : 'bg-orange-500';
    const saleEnabled = outlet.status === 'active' && (routeStatus?.status === 'on_field' || routeStatus?.status === 'settling') && firstPendingId === id;

    return (
      <div ref={setNodeRef} style={style} className={`${visited ? 'bg-green-50 border border-green-200' : 'bg-white'} p-4 rounded-lg shadow-sm`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            {outlet.outletSequence && (
              <div className={`flex-shrink-0 w-8 h-8 ${bubbleClass} text-white text-sm font-bold rounded-full flex items-center justify-center mt-1`}>
                {outlet.outletSequence}
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                {(() => {
                  const visit = outletVisits[id];
                  let cls = 'bg-blue-500';
                  if (visit) {
                    const hasSale = visit.status === 'sale' || visit.status === 'sale_and_replace';
                    const noSale = visit.status === 'no_sale';
                    cls = hasSale ? 'bg-green-500' : noSale ? 'bg-red-500' : 'bg-orange-500';
                  }
                  return <span className={`inline-block w-2 h-2 rounded-full ${cls}`}></span>;
                })()}
                <h4 className="font-semibold text-gray-800">{outlet.name}</h4>
                {((outlet as any).serialNumber || (outlet as any).id) && (
                  <span className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                    #{(outlet as any).serialNumber || (outlet as any).id}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mb-2">{outlet.address}</p>

              <div className="flex items-center space-x-4 text-sm">
                {outlet.mobile && (
                  <div className="flex items-center space-x-1">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">{outlet.mobile}</span>
                  </div>
                )}
                {outlet.latitude && outlet.longitude && (
                  <button 
                    onClick={() => {
                      const lat = encodeURIComponent(String(outlet.latitude));
                      const lng = encodeURIComponent(String(outlet.longitude));
                      const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
                      window.open(url, '_blank');
                    }}
                    className="flex items-center space-x-1 text-blue-600 hover:text-blue-700"
                  >
                    <Navigation className="w-4 h-4" />
                    <span>Directions</span>
                  </button>
                )}
                {/* Drag handle: attach listeners for dnd */}
                {!visited && (
                  <button
                    {...attributes}
                    {...listeners}
                    onClick={() => {
                      // On touch devices, show a brief hint to slide for drag
                      if (isTouch) {
                        setDragHintId(id);
                        setTimeout(() => setDragHintId(current => (current === id ? null : current)), 1500);
                      }
                    }}
                    className="flex items-center space-x-1 text-gray-500 hover:text-gray-700 active:cursor-grabbing cursor-grab select-none"
                    style={{ touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none' as any, WebkitTouchCallout: 'none' as any }}
                    title="Reorder (press and hold)"
                  >
                    <ArrowUpDown className="w-4 h-4" />
                    <span>Reorder</span>
                  </button>
                )}
                {dragHintId === id && (
                  <span className="text-xs text-gray-500">Drag to reorder</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={() => setEditingOutlet(outlet)}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              title="Edit Outlet"
            >
              <Edit className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Fourth line: Sale action (if enabled) and current status moved here */}
        <div className="flex items-center justify-between mt-2 text-sm">
          <div className="flex items-center space-x-3 pl-5">
            {saleEnabled && (
              <button
                onClick={() => { setPreselectedOutlet(outlet); setCurrentView('sales'); }}
                className="flex items-center space-x-1 text-green-600 hover:text-green-700"
                title="Start Sale"
              >
                <ShoppingCart className="w-4 h-4" />
                <span>Sale</span>
              </button>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${
              outlet.status === 'active' ? 'bg-green-500' :
              outlet.status === 'pending_approval' ? 'bg-yellow-500' :
              outlet.status === 'inactive' ? 'bg-red-500' : 'bg-gray-500'
            }`}></div>
            <span className={`text-sm ${
              outlet.status === 'active' ? 'text-green-600' :
              outlet.status === 'pending_approval' ? 'text-yellow-600' :
              outlet.status === 'inactive' ? 'text-red-600' : 'text-gray-600'
            }`}>
              {outlet.status === 'active' ? 'Active' :
               outlet.status === 'pending_approval' ? 'Pending Approval' :
               outlet.status === 'inactive' ? 'Inactive' : 
               outlet.status || 'Unknown'}
            </span>
            {(() => {
              const visit = outletVisits[id];
              if (!visit) return null;
              const hasSale = visit.status === 'sale' || visit.status === 'sale_and_replace';
              if (!hasSale) return null;
              return (
                <div className="ml-2 flex items-center space-x-1">
                  <span className={`text-xs px-2 py-1 rounded bg-green-100 text-green-700`}>
                    {`Sale ₹${(visit.saleAmount||0).toLocaleString()}`}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      loadSaleForEdit(outlets.find(o => o.id === id));
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Edit
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    );
  };

  // Static (non-sortable) item for visited outlets
  const StaticOutletItem: React.FC<{ outlet: any; firstPendingId?: string }> = ({ outlet, firstPendingId }) => {
    const id = (outlet as any).id as string;
    const visited = !!outletVisits[id];
    const bubbleClass = visited ? 'bg-green-600' : 'bg-orange-500';
    const saleEnabled = outlet.status === 'active' && (routeStatus?.status === 'on_field' || routeStatus?.status === 'settling') && firstPendingId === id;
    return (
      <div className={`${visited ? 'bg-green-50 border border-green-200' : 'bg-white'} p-4 rounded-lg shadow-sm`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            {outlet.outletSequence && (
              <div className={`flex-shrink-0 w-8 h-8 ${bubbleClass} text-white text-sm font-bold rounded-full flex items-center justify-center mt-1`}>
                {outlet.outletSequence}
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                {(() => {
                  const visit = outletVisits[id];
                  let cls = 'bg-blue-500';
                  if (visit) {
                    const hasSale = visit.status === 'sale' || visit.status === 'sale_and_replace';
                    const noSale = visit.status === 'no_sale';
                    cls = hasSale ? 'bg-green-500' : noSale ? 'bg-red-500' : 'bg-orange-500';
                  }
                  return <span className={`inline-block w-2 h-2 rounded-full ${cls}`}></span>;
                })()}
                <h4 className="font-semibold text-gray-800">{outlet.name}</h4>
                {((outlet as any).serialNumber || (outlet as any).id) && (
                  <span className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                    #{(outlet as any).serialNumber || (outlet as any).id}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mb-2">{outlet.address}</p>

              <div className="flex items-center space-x-4 text-sm">
                {outlet.mobile && (
                  <div className="flex items-center space-x-1">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">{outlet.mobile}</span>
                  </div>
                )}
                {outlet.latitude && outlet.longitude && (
                  <button 
                    onClick={() => {
                      const lat = encodeURIComponent(String(outlet.latitude));
                      const lng = encodeURIComponent(String(outlet.longitude));
                      const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
                      window.open(url, '_blank');
                    }}
                    className="flex items-center space-x-1 text-blue-600 hover:text-blue-700"
                  >
                    <Navigation className="w-4 h-4" />
                    <span>Directions</span>
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={() => setEditingOutlet(outlet)}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              title="Edit Outlet"
            >
              <Edit className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
        {/* Fourth line: Sale action (if enabled) and current status moved here */}
        <div className="flex items-center justify-between mt-2 text-sm">
          <div className="flex items-center space-x-3 pl-5">
            {saleEnabled && (
              <button
                onClick={() => { setPreselectedOutlet(outlet); setCurrentView('sales'); }}
                className="flex items-center space-x-1 text-green-600 hover:text-green-700"
                title="Start Sale"
              >
                <ShoppingCart className="w-4 h-4" />
                <span>Sale</span>
              </button>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${
              outlet.status === 'active' ? 'bg-green-500' :
              outlet.status === 'pending_approval' ? 'bg-yellow-500' :
              outlet.status === 'inactive' ? 'bg-red-500' : 'bg-gray-500'
            }`}></div>
            <span className={`text-sm ${
              outlet.status === 'active' ? 'text-green-600' :
              outlet.status === 'pending_approval' ? 'text-yellow-600' :
              outlet.status === 'inactive' ? 'text-red-600' : 'text-gray-600'
            }`}>
              {outlet.status === 'active' ? 'Active' :
               outlet.status === 'pending_approval' ? 'Pending Approval' :
               outlet.status === 'inactive' ? 'Inactive' : 
               outlet.status || 'Unknown'}
            </span>
            {(() => {
              const visit = outletVisits[id];
              if (!visit) return null;
              const hasSale = visit.status === 'sale' || visit.status === 'sale_and_replace';
              if (!hasSale) return null;
              return (
                <span className={`ml-2 text-xs px-2 py-1 rounded bg-green-100 text-green-700`}>
                  {`Sale ₹${(visit.saleAmount||0).toLocaleString()}`}
                </span>
              );
            })()}
          </div>
        </div>
      </div>
    );
  };

  // Handle drag end: persist new order for pending outlets only within allowed range
  const onDragEnd = async (event: DragEndEvent, orderedPendingIds: string[], firstPendingSeq: number) => {
    const { active, over } = event;
    setDraggingId(null);
    setActiveId(null);
    dragStartClientYRef.current = null;
    // Cleanup edge auto-scroll
    window.removeEventListener('pointermove', pointerMoveListener as any);
    window.removeEventListener('touchmove', touchMoveListener as any);
    stopAutoScrollLoop();
    if (!over || active.id === over.id) return;
    const oldIndex = orderedPendingIds.indexOf(String(active.id));
    const newIndex = orderedPendingIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(orderedPendingIds, oldIndex, newIndex);

    // Confirm drop intent before persisting
    const movedOutlet = outlets.find(o => (o as any).id === String(active.id));
    const toSeqPreview = firstPendingSeq + newIndex;
    const fromSeqPreview = (movedOutlet as any)?.outletSequence ?? (firstPendingSeq + oldIndex);
    const ok = window.confirm(`Move "${(movedOutlet as any)?.name || active.id}" from #${fromSeqPreview} to #${toSeqPreview}?`);
    if (!ok) return; // cancel persistence

    // Build batch updates mapping sequences starting from firstPendingSeq
    const updates: Array<{ docId: string; newSeq: number; localId: string }> = [];
    newOrder.forEach((oid, idx) => {
      const outlet = outlets.find(o => (o as any).id === oid);
      if (!outlet) return;
      const newSeq = firstPendingSeq + idx;
      if ((outlet as any).outletSequence !== newSeq) {
        const docId = (outlet as any).documentId;
        if (docId) updates.push({ docId, newSeq, localId: oid });
      }
    });

    if (updates.length === 0) return;

    const batch = writeBatch(db);
    updates.forEach(u => {
      const ref = doc(db, 'outlets', u.docId);
      batch.update(ref, { outletSequence: u.newSeq, updatedAt: serverTimestamp() });
    });
    await batch.commit();

    setOutlets(prev => {
      const map = new Map<string, any>();
      prev.forEach(p => map.set((p as any).id, { ...p }));
      updates.forEach(u => {
        const cur = map.get(u.localId);
        if (cur) (cur as any).outletSequence = u.newSeq;
      });
      const arr = Array.from(map.values());
      arr.sort((a: any, b: any) => {
        const as = a.outletSequence ?? 9999;
        const bs = b.outletSequence ?? 9999;
        if (as !== bs) return as - bs;
        return (a.name || '').localeCompare(b.name || '');
      });
      return arr;
    });
    // Nothing else to reset here
  };

  const onDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    setDraggingId(id);
    setActiveId(id);
    // Record starting clientY from the original activator event for manual auto-scroll
    const ae: any = (event as any).activatorEvent;
    let y: number | null = null;
    if (ae) {
      if (ae.touches && ae.touches[0]) y = ae.touches[0].clientY;
      else if (typeof ae.clientY === 'number') y = ae.clientY;
    }
    dragStartClientYRef.current = y;
    // Initialize live pointer tracking and listeners for edge-triggered scroll
    pointerClientYRef.current = y;
    // Immediately evaluate edge zones on start (in case finger starts at edge)
    handleGlobalPointerMove(y);
    window.addEventListener('pointermove', pointerMoveListener as any, { passive: true } as any);
    window.addEventListener('touchmove', touchMoveListener as any, { passive: true } as any);
  };

  // No-op: we let dnd-kit handle sibling transforms during drag; we persist only on drop
  const onDragMove = (event: DragMoveEvent) => {
    // Fallback feed: compute current clientY from initial + delta to trigger scroll
    const startY = dragStartClientYRef.current;
    if (startY == null) return;
    const currentY = startY + event.delta.y;
    handleGlobalPointerMove(currentY);
  };

  // Load existing sale data for editing
  const loadSaleForEdit = async (outlet: any) => {
    try {
      const today = getTodayDateStringIST();
      
      // Load sales header
      const salesHeaderQuery = query(
        collection(db, 'salesHeaders'),
        where('outletId', '==', outlet.id),
        where('date', '==', today),
        where('salesmanId', '==', currentUser?.id || '')
      );
      const headerSnapshot = await getDocs(salesHeaderQuery);
      
      if (headerSnapshot.empty) {
        console.error('No sale found for this outlet today');
        alert('No sale found for this outlet today.');
        return;
      }
      
      const headerData = headerSnapshot.docs[0].data();
      
      // Load sales details
      const salesDetailsQuery = query(
        collection(db, 'salesDetails'),
        where('outletId', '==', outlet.id),
        where('date', '==', today)
      );
      const detailsSnapshot = await getDocs(salesDetailsQuery);
      
      const salesDetails = detailsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setEditingSale({
        outlet,
        transactionId: headerData.transactionId,
        salesDetails,
        discount: headerData.discount || 0
      });
      
      setCurrentView('sales');
      
    } catch (error) {
      console.error('Error loading sale for edit:', error);
      alert('Error loading sale data. Please try again.');
    }
  };

  // Helper: refresh outlet visits data
  const refreshOutletVisits = async () => {
    if (!currentUser?.id) return;
    
    try {
      const today = getTodayDateStringIST();
      const visitQuery = query(
        collection(db, 'outletVisits'),
        where('salesmanId', '==', currentUser.id),
        where('date', '==', today)
      );
      const visitSnapshot = await getDocs(visitQuery);
      const visitMap: { [outletId: string]: any } = {};
      visitSnapshot.docs.forEach(doc => {
        const visitData = doc.data();
        visitMap[visitData.outletId] = visitData;
      });
      setOutletVisits(visitMap);
      console.log('🔄 Refreshed outlet visits data');
    } catch (error) {
      console.error('Error refreshing outlet visits:', error);
    }
  };

  // Fetch outlets and visit data from Firebase
  useEffect(() => {
    const fetchOutlets = async () => {
      try {
        setLoading(true);
        
        if (currentUser?.role === 'salesman') {
          // Use shared outlet filtering function - show ALL outlets in Dashboard
          const result = await getFilteredOutletsForUser(currentUser.id, 'all');
          
          if (result.error) {
            console.log('❌ Outlet filtering error:', result.error);
            setOutlets([]);
            setOutletVisits({});
          } else {
            console.log(`✅ Loaded ${result.outlets.length} outlets for route "${result.userRoute.name}"`);
            
            // Load outlet visits for today to show visit status
            await refreshOutletVisits();
            
            setOutlets(result.outlets);
          }
        } else {
          // For non-salesmen, show all outlets
          const allOutlets = await getDocuments('outlets');
          const sortedOutlets = allOutlets.sort((a, b) => {
            if (a.outletSequence && b.outletSequence) {
              return a.outletSequence - b.outletSequence;
            }
            if (a.outletSequence && !b.outletSequence) return -1;
            if (!a.outletSequence && b.outletSequence) return 1;
            return (a.name || '').localeCompare(b.name || '');
          });
          setOutlets(sortedOutlets);
        }
        
        // Update stats based on real data for today
        try {
          const today = getTodayDateStringIST();
          const salesQueryRef = query(
            collection(db, 'salesHeaders'),
            where('date', '==', today),
            where('status', '==', 'completed'),
            where('salesmanId', '==', currentUser?.id || '')
          );
          const salesSnap = await getDocs(salesQueryRef);
          const totalSales = salesSnap.docs.reduce((sum, d) => sum + ((d.data() as any).total || 0), 0);
          const totalCashCollected = salesSnap.docs
            .filter(d => (d.data() as any).txnMode === 'cash')
            .reduce((sum, d) => sum + ((d.data() as any).total || 0), 0);

          // Visits today
          const visitQ = query(
            collection(db, 'outletVisits'),
            where('salesmanId', '==', currentUser?.id || ''),
            where('date', '==', today)
          );
          const visitSnap = await getDocs(visitQ);
          const outletsVisited = visitSnap.size;

          // Simple target assumption placeholder (12,000) until wired to config
          const target = 12000;
          const targetAchievement = target > 0 ? Math.min(100, Math.round((totalSales / target) * 100)) : 0;

          setStats(prev => ({
            ...prev,
            totalSales,
            totalCashCollected,
            outletsVisited,
            targetAchievement
          }));
        } catch (e) {
          console.warn('Failed to compute real stats, keeping previous values');
        }
      } catch (error) {
        console.error('Error fetching outlets:', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchOutlets();
    }
  }, [currentUser]);

  // Get current location for Routes map view
  useEffect(() => {
    if (currentView === 'routes') {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setCurrentLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          },
          (error) => {
            console.error('Error getting current location:', error);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000
          }
        );
      }
    }
  }, [currentView]);

  // Keep bottom nav highlighting in sync with the current view
  useEffect(() => {
    if (currentView === 'dashboard') setActiveTab('dashboard');
    else if (currentView === 'inventory') setActiveTab('inventory');
    else if (currentView === 'routes') setActiveTab('routes');
    else if (currentView === 'outlets') setActiveTab('outlets');
  }, [currentView]);

  // 🔧 FIX: Real-time route status tracking for salesman with all statuses
  useEffect(() => {
    if (currentUser?.role !== 'salesman') return;

    let unsubscribe: (() => void) | null = null;

    const setupRouteStatusListener = async () => {
      try {
        // Get today's date in IST
        const today = getTodayDateStringIST();

        // Find the salesman's beat assignment to get route ID
        const beatsQuery = query(
          collection(db, 'beats'),
          where('salesmanId', '==', currentUser.id),
          where('forDate', '==', today),
          where('isActive', '==', true)
        );
        const beatsSnapshot = await getDocs(beatsQuery);

        if (beatsSnapshot.empty) {
          console.log('No beat assignment found for today');
          setRouteStatus(null);
          return;
        }

        const beat = beatsSnapshot.docs[0].data();
        const assignedRouteId: string | undefined = beat.routeId;

        if (!assignedRouteId) {
          console.log('No route assigned to beat');
          setRouteStatus(null);
          return;
        }

        // 🔧 KEY FIX: Set up real-time listener for this specific route's status
        const routeStatusQuery = query(
          collection(db, 'route_daily_status'),
          where('routeId', '==', assignedRouteId),
          where('date', '==', today)
        );

        unsubscribe = onSnapshot(routeStatusQuery, (snapshot) => {
          if (!snapshot.empty) {
            const statusDoc = snapshot.docs[0];
            const status = { id: statusDoc.id, ...statusDoc.data() } as RouteDailyStatus;
            
            console.log(`🔄 Real-time route status update:`, {
              routeId: assignedRouteId,
              status: status.status,
              routeName: (status as any).routeName
            });
            
            setRouteStatus(status);
          } else {
            console.log('No route status found for today');
            setRouteStatus(null);
          }
        }, (error) => {
          console.error('Error in route status listener:', error);
        });

      } catch (error) {
        console.error('Error setting up route status listener:', error);
      }
    };

    setupRouteStatusListener();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser]);

  // 🔧 IMPROVED: Smart function to handle settlement status changes with proper validation
  const handleReadyForSettlement = async () => {
    if (!currentUser || !routeStatus) return;

    // 🔧 KEY FIX: Prevent action if route is already settled or settling
    if (routeStatus.status === 'settled') {
      alert('This route has already been settled for today.');
      return;
    }

    if (routeStatus.status === 'settling') {
      alert('This route is already marked for settlement. Please wait for the warehouse team to process it.');
      return;
    }

    if (routeStatus.status !== 'on_field') {
      alert('Route must be on field to mark ready for settlement.');
      return;
    }

    setProcessingSettlement(true);
    
    try {
  // Get today's date (IST)
  const today = getTodayDateStringIST();

      // Find the salesman's beat assignment for today
      const beatsQuery = query(
        collection(db, 'beats'),
        where('salesmanId', '==', currentUser.id),
        where('forDate', '==', today),
        where('isActive', '==', true)
      );
      const beatsSnapshot = await getDocs(beatsQuery);

      if (beatsSnapshot.empty) {
        alert('No beat assignment found for today. Please contact your supervisor.');
        return;
      }

      const beat = beatsSnapshot.docs[0].data();
      const assignedRouteId: string | undefined = beat.routeId;

      if (!assignedRouteId) {
        alert('No route assigned to your beat today. Please contact your supervisor.');
        return;
      }

      // Find the route daily status for today
      const routeStatusQuery = query(
        collection(db, 'route_daily_status'),
        where('routeId', '==', assignedRouteId),
        where('date', '==', today)
      );
      const routeStatusSnapshot = await getDocs(routeStatusQuery);

      if (routeStatusSnapshot.empty) {
        alert('No route status found for today. Please contact your supervisor.');
        return;
      }

      const routeStatusDoc = routeStatusSnapshot.docs[0];
      const routeStatusData = routeStatusDoc.data();

      // Double-check status before updating (race condition protection)
      if (routeStatusData.status === 'settled') {
        alert('This route has already been settled by the warehouse team.');
        return;
      }

      if (routeStatusData.status === 'settling') {
        alert('This route is already being processed for settlement.');
        return;
      }

      // Update route status to 'settling'
      await setDoc(routeStatusDoc.ref, {
        ...routeStatusData,
        status: 'settling',
        settlingAt: serverTimestamp(),
        settlingBy: currentUser.id,
        lastUpdatedBy: currentUser.id,
        updatedAt: serverTimestamp()
      });

      alert('✅ Route marked as ready for settlement! The warehouse team can now process your settlement.');
    } catch (error) {
      console.error('Error updating route status:', error);
      alert('Failed to update settlement status. Please try again.');
    } finally {
      setProcessingSettlement(false);
    }
  };

  // Load Google Maps API dynamically (from AddOutlet's robust version)
  const loadGoogleMaps = () => {
    return new Promise((resolve, reject) => {
      // Check if Google Maps is already loaded
      if ((window as any).google && (window as any).google.maps) {
        resolve(true);
        return;
      }
      
      // Check if script is already loading
      if ((window as any).googleMapsLoading) {
        const checkInterval = setInterval(() => {
          if ((window as any).google && (window as any).google.maps) {
            clearInterval(checkInterval);
            resolve(true);
          }
        }, 100);
        return;
      }
      
      // Mark as loading
      (window as any).googleMapsLoading = true;
      
      // Create script element
      const script = document.createElement('script');
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      
      if (!apiKey) {
        reject(new Error('Google Maps API key not found'));
        return;
      }
      
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        (window as any).googleMapsLoading = false;
        resolve(true);
      };
      
      script.onerror = () => {
        (window as any).googleMapsLoading = false;
        reject(new Error('Failed to load Google Maps API'));
      };
      
      document.head.appendChild(script);
    });
  };

  const cleanupMap = () => {
    try {
      // Clean up cluster first
      if (clusterRef.current) {
        clusterRef.current.clearMarkers();
        clusterRef.current = null;
      }

      // Just disconnect the map and markers
      markersRef.current.forEach(marker => {
        if (marker?.setMap) marker.setMap(null);
      });
      markersRef.current = [];

      // Remove polylines
      polylinesRef.current.forEach(line => {
        if (line?.setMap) line.setMap(null);
      });
      polylinesRef.current = [];

      // Remove directions renderer
      if (directionsRendererRef.current) {
        try {
          directionsRendererRef.current.setMap(null);
        } catch {}
        directionsRendererRef.current = null;
      }

      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }

      setMapLoaded(false);
    } catch (error) {
      console.error("Error during map cleanup:", error);
    }
  };

  // Reset map when switching away from map view
  useEffect(() => {
    const inRoutesMap = currentView === 'routes';
    if (!inRoutesMap) {
      cleanupMap();
    }
  }, [currentView]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanupMap();
    };
  }, []);

  // Initialize Google Maps when map view is selected (Outlets map tab or Routes view)
  useEffect(() => {
    const shouldInit = (currentView === 'routes') && outlets.length > 0 && !mapLoaded;
    if (shouldInit) {
      const initializeMap = async () => {
        const mapElement = mapContainerRef.current;
        if (!mapElement) return;

        try {
          await loadGoogleMaps();

          // Wait for Google Maps to be fully available
          let retries = 0;
          while (retries < 10) {
            const google = (window as any).google;
            if (google?.maps?.Map && typeof google.maps.Map === 'function') {
              break;
            }
            await new Promise(resolve => setTimeout(resolve, 100));
            retries++;
          }

          const google = (window as any).google;
          if (!google?.maps?.Map || typeof google.maps.Map !== 'function') {
            console.error('Google Maps API not loaded properly');
            return;
          }

          // Build center point from valid outlets only
          const validOutlets = outlets.filter(o => o.latitude && o.longitude);
          if (validOutlets.length === 0) return;

          const center = validOutlets.reduce(
            (acc, outlet) => {
              acc.lat += parseFloat(outlet.latitude);
              acc.lng += parseFloat(outlet.longitude);
              return acc;
            },
            { lat: 0, lng: 0 }
          );

          center.lat /= validOutlets.length;
          center.lng /= validOutlets.length;

          // Use current location as center if available, otherwise use outlets center
          let mapCenter = { lat: center.lat, lng: center.lng };
          let initialZoom = 14;
          
          if (currentLocation) {
            mapCenter = currentLocation;
            initialZoom = 16; // ~100m zoom level
          }

          const map = new google.maps.Map(mapElement, {
            center: mapCenter,
            zoom: initialZoom,
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels.text.fill',
                stylers: [{ color: '#757575' }]
              }
            ]
          });

          mapInstanceRef.current = map;

          const newMarkers: any[] = [];

          // Add current location marker if available
          if (currentLocation) {
            const currentLocationMarker = new google.maps.Marker({
              position: currentLocation,
              title: 'Your Location',
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: '#3B82F6',
                fillOpacity: 1,
                strokeColor: '#1E40AF',
                strokeWeight: 3,
                scale: 10
              },
              zIndex: 1000 // Ensure it appears on top
            });

            const currentLocationInfo = new google.maps.InfoWindow({
              content: `
                <div style="padding: 8px;">
                  <h3 style="font-weight: 600; color: #1f2937; margin-bottom: 4px;">📍 Your Location</h3>
                  <p style="font-size: 14px; color: #4b5563;">Current position</p>
                </div>
              `
            });

            currentLocationMarker.addListener('click', () => {
              currentLocationInfo.open(map, currentLocationMarker);
            });
            if (currentLocationMarker?.setMap) currentLocationMarker.setMap(map);
            newMarkers.push(currentLocationMarker);
          }

          // Sort outlets by outletSequence then name for route drawing
          const sortedOutlets = [...validOutlets].sort((a, b) => {
            if (a.outletSequence && b.outletSequence) return a.outletSequence - b.outletSequence;
            if (a.outletSequence && !b.outletSequence) return -1;
            if (!a.outletSequence && b.outletSequence) return 1;
            return (a.name || '').localeCompare(b.name || '');
          });

          // Add outlet markers with color coding and sequence labels
          sortedOutlets.forEach(outlet => {
            const outletKey = (outlet as any).id;
            const visit = outletVisits[outletKey];
            const isVisited = !!visit;
            const hasSale = visit?.status === 'sale' || visit?.status === 'sale_and_replace';
            const isNoSale = visit?.status === 'no_sale';
            
            // Color precedence: inactive > pending_approval > visited > pending
            let fillColor = '#F59E0B'; // pending default (orange)
            let strokeColor = '#D97706';
            let statusText = 'Pending';
            let statusColor = '#F59E0B';
            if (outlet.status === 'inactive') {
              fillColor = '#9CA3AF'; // gray-400
              strokeColor = '#6B7280'; // gray-500
              statusText = 'Inactive';
              statusColor = '#6B7280';
            } else if (outlet.status === 'pending_approval') {
              fillColor = '#60A5FA'; // blue-400
              strokeColor = '#3B82F6'; // blue-600
              statusText = 'Pending Approval';
              statusColor = '#3B82F6';
            } else if (isVisited) {
              fillColor = '#10B981';
              strokeColor = '#059669';
              statusText = 'Visited';
              statusColor = '#10B981';
            }

            const marker = new google.maps.Marker({
              position: {
                lat: parseFloat(outlet.latitude),
                lng: parseFloat(outlet.longitude),
              },
              title: outlet.name,
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: fillColor,
                fillOpacity: 1,
                strokeColor: strokeColor,
                strokeWeight: 2,
                scale: 8
              },
              label: outlet.outletSequence ? {
                text: String(outlet.outletSequence),
                color: '#ffffff',
                fontSize: '10px',
                fontWeight: 'bold'
              } : undefined
            });

            const containerStyle = `padding: 12px; ${isVisited ? 'background-color: #ECFDF5; border: 1px solid #10B981; border-radius: 8px;' : ''}`;
            const infoWindow = new google.maps.InfoWindow({
              content: `
                <div style="${containerStyle}">
                  <h3 style="font-weight: 600; color: #1f2937; margin-bottom: 4px;">${outlet.name}</h3>
                  <p style="font-size: 14px; color: #4b5563; margin-bottom: 8px;">${outlet.address}</p>
                  ${outlet.mobile ? `<p style="font-size: 14px; color: #4b5563;">📞 ${outlet.mobile}</p>` : ''}
                  <div style="display: flex; align-items: center; margin-top: 8px; margin-bottom: 8px;">
                    <div style="width: 8px; height: 8px; background-color: ${fillColor}; border-radius: 50%; margin-right: 8px;"></div>
                    <span style="font-size: 14px; color: ${statusColor};">${statusText}</span>
                  </div>
                  ${visit?.saleAmount ? `<p style=\"font-size: 12px; color: #059669; margin-bottom: 4px;\">Sale: ₹${visit.saleAmount.toLocaleString()}</p>` : ''}
                  ${visit?.noSaleReason ? `<p style=\"font-size: 12px; color: #DC2626; margin-bottom: 8px;\">Reason: ${visit.noSaleReason}</p>` : ''}
                  <button 
                    id="edit-outlet-${outletKey}" 
                    style="
                      background-color: #3B82F6; 
                      color: white; 
                      border: none; 
                      padding: 8px 16px; 
                      border-radius: 6px; 
                      font-size: 12px; 
                      cursor: pointer; 
                      display: flex; 
                      align-items: center; 
                      gap: 4px;
                      font-weight: 500;
                      transition: background-color 0.2s;
                      margin-top: 8px;
                      width: 100%;
                      justify-content: center;
                    "
                    onmouseover="this.style.backgroundColor='#2563EB'"
                    onmouseout="this.style.backgroundColor='#3B82F6'"
                  >
                    ✏️ Edit Outlet
                  </button>
                </div>
              `
            });

            // Set up event listener for the InfoWindow's domready event to attach button click handler
            google.maps.event.addListener(infoWindow, 'domready', () => {
              const editButton = document.getElementById(`edit-outlet-${outletKey}`);
              if (editButton) {
                editButton.addEventListener('click', () => {
                  // Close the InfoWindow
                  infoWindow.close();
                  // Trigger edit mode
                  setEditingOutlet(outlet);
                });
              }
            });

            marker.addListener('click', () => {
              infoWindow.open(map, marker);
            });

            // Add to map directly (no clustering)
            if (marker?.setMap) marker.setMap(map);
            newMarkers.push(marker);
          });

          // Draw road route following outlet sequence (Directions API), fallback to straight line
          if (sortedOutlets.length >= 2) {
            try {
              const origin = {
                lat: parseFloat(sortedOutlets[0].latitude),
                lng: parseFloat(sortedOutlets[0].longitude)
              };
              const destination = {
                lat: parseFloat(sortedOutlets[sortedOutlets.length - 1].latitude),
                lng: parseFloat(sortedOutlets[sortedOutlets.length - 1].longitude)
              };
              const inner = sortedOutlets.slice(1, sortedOutlets.length - 1);
              const waypoints = inner.slice(0, Math.max(0, 23)).map(o => ({
                location: { lat: parseFloat(o.latitude), lng: parseFloat(o.longitude) },
                stopover: true
              }));

              const ds = new google.maps.DirectionsService();
              const dr = new google.maps.DirectionsRenderer({
                map,
                suppressMarkers: true,
                preserveViewport: true,
                polylineOptions: { strokeColor: '#2563EB', strokeWeight: 4 }
              });

              ds.route(
                {
                  origin,
                  destination,
                  waypoints,
                  optimizeWaypoints: false,
                  travelMode: google.maps.TravelMode.DRIVING
                },
                (result: any, status: any) => {
                  if (status === 'OK' && result) {
                    dr.setDirections(result);
                    directionsRendererRef.current = dr;
                  } else {
                    const path = sortedOutlets.map(o => ({
                      lat: parseFloat(o.latitude),
                      lng: parseFloat(o.longitude)
                    }));
                    const polyline = new google.maps.Polyline({
                      path,
                      strokeColor: '#2563EB',
                      strokeOpacity: 0.9,
                      strokeWeight: 3
                    });
                    polyline.setMap(map);
                    polylinesRef.current.push(polyline);
                  }
                }
              );
            } catch (e) {
              const path = sortedOutlets.map(o => ({
                lat: parseFloat(o.latitude),
                lng: parseFloat(o.longitude)
              }));
              const polyline = new google.maps.Polyline({
                path,
                strokeColor: '#2563EB',
                strokeOpacity: 0.9,
                strokeWeight: 3
              });
              polyline.setMap(map);
              polylinesRef.current.push(polyline);
            }
          }

          // No clustering: keep marker refs for cleanup only
          markersRef.current = newMarkers;
          setMapLoaded(true);
        } catch (error) {
          console.error('Error initializing map:', error);
        }
      };

      // Always defer the map init with setTimeout
      setTimeout(() => {
        initializeMap();
      }, 100);
    }
  }, [currentView, outlets, mapLoaded]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Confirm loadout and move to on_field (with inventory transfer)
  const handleConfirmOnField = async () => {
    if (!currentUser || !routeStatus) return;
    if (routeStatus.status !== 'issuing') return;
    setConfirmingOnField(true);
    try {
      // Today's date (IST)
  const today = getTodayDateStringIST();

      // Resolve beat to get vehicleId and routeId
      const beatsQueryRef = query(
        collection(db, 'beats'),
        where('salesmanId', '==', currentUser.id),
        where('forDate', '==', today),
        where('isActive', '==', true)
      );
      const beatsSnapshot = await getDocs(beatsQueryRef);
      if (beatsSnapshot.empty) {
        alert('No active beat found for today.');
        return;
      }
      const beat = beatsSnapshot.docs[0].data() as any;
      const assignedRouteId = beat.routeId as string | undefined;
      if (!assignedRouteId) {
        alert('Missing route assignment.');
        return;
      }

      // Find issuing route_daily_status doc
      const rdsQuery = query(
        collection(db, 'route_daily_status'),
        where('routeId', '==', assignedRouteId),
        where('date', '==', today),
        where('status', '==', 'issuing')
      );
      const rdsSnapshot = await getDocs(rdsQuery);
      if (rdsSnapshot.empty) {
        alert('No pending loadout found to confirm.');
        return;
      }
      const rdsDoc = rdsSnapshot.docs[0];
      const rds = rdsDoc.data() as any;
      const parentWarehouseId = rds.warehouseId || '';
      if (!parentWarehouseId) {
        alert('Route status missing source warehouse.');
        return;
      }
      // Require vehicle selection before confirmation
      const vehicleOnStatus: string | undefined = (rds as any).vehicleId;
      if (!vehicleOnStatus && !selectedVehicleId) {
        alert('Please select a vehicle on the Home page before confirming loadout.');
        return;
      }

      if (!Array.isArray(rds.detailedAllocations) || rds.detailedAllocations.length === 0) {
        alert('No allocation details found. Please re-issue loadout.');
        return;
      }

      // Build items for transfer
      const transactionItems = (rds.detailedAllocations as any[])
        .map(a => ({
          productId: a.productId,
          productName: a.productName || a.productId,
          unit: a.unit || 'KG',
          quantity: (a.issuedQty ?? a.allocatedQty) || 0,
          rate: a.rate || 0
        }))
        .filter(it => it.quantity > 0);

      const result = await UnifiedInventoryTransactionService.processLoadoutTransaction(
        parentWarehouseId,
        assignedRouteId,
        transactionItems,
        rds.beatId || rdsDoc.id,
        currentUser.id
      );
      if (!result.success) {
        alert('Failed to transfer inventory. Please try again.');
        return;
      }

      await updateDoc(rdsDoc.ref, {
        status: 'on_field',
        confirmedAt: serverTimestamp(),
        confirmedBy: currentUser.id,
        salesmanId: currentUser.id,
        updatedAt: serverTimestamp()
      });

      alert('Loadout confirmed. You are now On Field.');
    } catch (e) {
      console.error(e);
      alert('Error confirming loadout.');
    } finally {
      setConfirmingOnField(false);
    }
  };

  const handleToggleSettle = async () => {
    if (!currentUser || !routeStatus) return;
    // Toggle between on_field and settling
    try {
  const today = getTodayDateStringIST();

      const beatsQueryRef = query(
        collection(db, 'beats'),
        where('salesmanId', '==', currentUser.id),
        where('forDate', '==', today),
        where('isActive', '==', true)
      );
      const beatsSnapshot = await getDocs(beatsQueryRef);
      if (beatsSnapshot.empty) return;
      const beat = beatsSnapshot.docs[0].data() as any;
      const assignedRouteId = beat.routeId as string | undefined;
      if (!assignedRouteId) return;

      const routeStatusQuery = query(
        collection(db, 'route_daily_status'),
        where('routeId', '==', assignedRouteId),
        where('date', '==', today)
      );
      const routeStatusSnapshot = await getDocs(routeStatusQuery);
      if (routeStatusSnapshot.empty) return;
      const routeStatusDoc = routeStatusSnapshot.docs[0];
      const current = routeStatusDoc.data() as any;

      if (current.status === 'on_field') {
        await updateDoc(routeStatusDoc.ref, {
          status: 'settling',
          settlingAt: serverTimestamp(),
          settlingBy: currentUser.id,
          lastUpdatedBy: currentUser.id,
          updatedAt: serverTimestamp()
        });
      } else if (current.status === 'settling') {
        await updateDoc(routeStatusDoc.ref, {
          status: 'on_field',
          lastUpdatedBy: currentUser.id,
          updatedAt: serverTimestamp()
        });
      }
    } catch (e) {
      console.error('Toggle settle failed', e);
    }
  };

  // Role-based dashboard rendering
  if (currentUser?.role === 'warehouse') {
    // Handle warehouse views
    if (currentView === 'inventory') {
      return <InventoryManagement onBack={() => setCurrentView('dashboard')} />;
    }
    
    return (
      <WarehouseDashboard 
        onShowInventory={() => setCurrentView('inventory')}
      />
    );
  }

  // Handle salesman views
  if (currentView === 'inventory') {
    return <InventoryManagement onBack={() => setCurrentView('dashboard')} />;
  }

  if (currentView === 'sales') {
    return <Sales 
      onBack={() => { 
        setCurrentView(activeTab); // Return to current tab instead of 'dashboard'
        setPreselectedOutlet(null);
        setEditingSale(null); // Clear edit mode
        refreshOutletVisits(); // Refresh visit status after returning from sales
      }} 
      initialOutlet={preselectedOutlet || editingSale?.outlet || null}
      editMode={editingSale}
    />;
  }

  if (currentView === 'addOutlet' || editingOutlet) {
    return <AddOutlet 
      editMode={!!editingOutlet}
      existingOutlet={editingOutlet}
      onBack={() => {
        setEditingOutlet(null);
        setCurrentView(editingOutlet ? 'outlets' : 'dashboard');
        // If we earmarked a slot for insertion, shift sequences and place the new outlet there
        if (insertAtSeq != null) {
          (async () => {
            try {
              const allOutlets = await getDocuments('outlets');
              // Find the newest outlet without a sequence to assign
              const recent = [...allOutlets]
                .filter((o: any) => typeof o.outletSequence !== 'number')
                .sort((a: any, b: any) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))[0];
              if (!recent) return;
              const batch = writeBatch(db);
              const toShift = allOutlets.filter((o: any) => typeof o.outletSequence === 'number' && o.outletSequence >= insertAtSeq);
              toShift.forEach((o: any) => {
                if (o.documentId) batch.update(doc(db, 'outlets', o.documentId), { outletSequence: o.outletSequence + 1, updatedAt: serverTimestamp() });
              });
              if (recent.documentId) {
                batch.update(doc(db, 'outlets', recent.documentId), { outletSequence: insertAtSeq, updatedAt: serverTimestamp() });
              }
              await batch.commit();
              setInsertAtSeq(null);
              const refreshed = await getDocuments('outlets');
              const sorted = refreshed.sort((a: any, b: any) => {
                const as = a.outletSequence ?? 9999;
                const bs = b.outletSequence ?? 9999;
                if (as !== bs) return as - bs;
                return (a.name || '').localeCompare(b.name || '');
              });
              setOutlets(sorted);
            } catch (e) {
              console.error('Post-insert sequencing failed', e);
            }
          })();
        } else {
          // Regular refresh
          if (currentUser) {
            const fetchOutlets = async () => {
              try {
                const allOutlets = await getDocuments('outlets');
                setOutlets(allOutlets.sort((a: any, b: any) => {
                  const as = a.outletSequence ?? 9999;
                  const bs = b.outletSequence ?? 9999;
                  if (as !== bs) return as - bs;
                  return (a.name || '').localeCompare(b.name || '');
                }));
              } catch (error) {
                console.error('Error refreshing outlets:', error);
              }
            };
            fetchOutlets();
          }
        }
      }} 
    />;
  }

  if (currentView === 'outlets') {
    return (
      <div className="mobile-container bg-gray-50 min-h-screen">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  setCurrentView('dashboard');
                  setActiveTab('dashboard');
                }}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <Home className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center space-x-2">
                <MapPin className="w-6 h-6 text-blue-600" />
                <h1 className="text-lg font-semibold text-gray-800">My Outlets</h1>
              </div>
            </div>
            {/* Removed header Add New per request; now rendered inline between visited and next outlet */}
          </div>
        </header>

        {/* Content Area - List view only */}
        <div className="pb-20">
            <div className="p-4 space-y-4">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : outlets.length === 0 ? (
                <div className="text-center py-12">
                  <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">No Outlets Yet</h3>
                  <p className="text-gray-600 mb-4">Start by adding your first outlet to begin tracking sales and visits.</p>
                  <button 
                    onClick={() => setCurrentView('addOutlet')}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Add First Outlet
                  </button>
                </div>
              ) : (
                <>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Total Outlets: {outlets.length}</h3>
                    <p className="text-sm text-gray-600">Manage your outlet network</p>
                  </div>

                  <div className="space-y-3">
                    {(() => {
                      // Unified sortable list (visited + pending) to allow crossing boundary during drag
                      const { firstPendingId, firstPendingSeq } = getFirstPendingInfo();
                      const measuring = { droppable: { strategy: MeasuringStrategy.Always } } as any;
                      const allIds = outlets.map(o => (o as any).id);
                      // Choose a stable baseline for sequences across all items (default to 1)
                      const minSeq = (() => {
                        const seqs = outlets
                          .map(o => (o as any).outletSequence)
                          .filter((n: any) => typeof n === 'number') as number[];
                        return seqs.length ? Math.min(...seqs) : 1;
                      })();

                      return (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragStart={onDragStart}
                          onDragMove={onDragMove}
                          onDragEnd={async (e) => {
                            await onDragEnd(e, allIds, minSeq);
                          }}
                          measuring={measuring}
                        >
                          <SortableContext items={allIds} strategy={verticalListSortingStrategy}>
                            {outlets.map((outlet, idx) => {
                              const oid = (outlet as any).id as string;
                              const isFirstPending = firstPendingId && oid === firstPendingId;
                              return (
                                <React.Fragment key={oid}>
                                  {/* Inline Add New card just before first pending outlet */}
                                  {isFirstPending && (
                                    <div className="flex justify-center my-2">
                                      <button
                                        onClick={() => {
                                          // Set insertion sequence to the first pending sequence (or fallback to next integer)
                                          const seq = typeof firstPendingSeq === 'number' ? firstPendingSeq : (minSeq + idx);
                                          setInsertAtSeq(seq);
                                          setCurrentView('addOutlet');
                                        }}
                                        className="px-4 py-2 rounded-lg border border-dashed border-blue-400 text-blue-600 bg-blue-50 hover:bg-blue-100 text-sm"
                                      >
                                        + Add New Outlet here
                                      </button>
                                    </div>
                                  )}
                                  <SortableOutletItem outlet={outlet} firstPendingId={firstPendingId} />
                                </React.Fragment>
                              );
                            })}
                            {/* If all outlets are visited (no firstPending), show the add button at end */}
                            {!firstPendingId && (
                              <div className="flex justify-center my-2">
                                <button
                                  onClick={() => {
                                    // Place at the end: choose next sequence after max existing
                                    const seqs = outlets.map(o => (o as any).outletSequence).filter((n: any) => typeof n === 'number') as number[];
                                    const nextSeq = seqs.length ? Math.max(...seqs) + 1 : 1;
                                    setInsertAtSeq(nextSeq);
                                    setCurrentView('addOutlet');
                                  }}
                                  className="px-4 py-2 rounded-lg border border-dashed border-blue-400 text-blue-600 bg-blue-50 hover:bg-blue-100 text-sm"
                                >
                                  + Add New Outlet here
                                </button>
                              </div>
                            )}
                          </SortableContext>
                          {/* Drag overlay for smooth lift while siblings yield */}
                          <DragOverlay>
                            {activeId ? (() => {
                              const outlet = outlets.find(o => (o as any).id === activeId);
                              if (!outlet) return null;
                              const visited = !!outletVisits[activeId];
                              const bubbleClass = visited ? 'bg-green-600' : 'bg-orange-500';
                              return (
                                <div className={`${visited ? 'bg-green-50 border border-green-200' : 'bg-white'} p-4 rounded-lg shadow-md`}
                                     style={{ boxShadow: '0 8px 16px rgba(0,0,0,0.2)' }}>
                                  <div className="flex items-start space-x-3">
                                    {outlet.outletSequence && (
                                      <div className={`flex-shrink-0 w-8 h-8 ${bubbleClass} text-white text-sm font-bold rounded-full flex items-center justify-center mt-1`}>
                                        {outlet.outletSequence}
                                      </div>
                                    )}
                                    <div>
                                      <h4 className="font-semibold text-gray-800">{outlet.name}</h4>
                                      <p className="text-sm text-gray-600">{outlet.address}</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })() : null}
                          </DragOverlay>
                        </DndContext>
                      );
                    })()}
                  </div>
                </>
              )}
            </div>
          
        </div>

        {/* Bottom Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 max-w-md mx-auto">
          <div className="flex justify-around py-2">
            <button 
              onClick={() => {
                setCurrentView('dashboard');
                setActiveTab('dashboard');
              }}
              className={`flex flex-col items-center py-2 px-4 ${
                activeTab === 'dashboard' ? 'text-blue-600' : 'text-gray-400'
              }`}
            >
              <Home className="w-6 h-6" />
              <span className="text-xs mt-1">Home</span>
            </button>
            <button 
              onClick={() => {
                setCurrentView('inventory');
                setActiveTab('inventory');
              }}
              className={`flex flex-col items-center py-2 px-4 ${
                activeTab === 'inventory' ? 'text-blue-600' : 'text-gray-400'
              }`}
            >
              <Package className="w-6 h-6" />
              <span className="text-xs mt-1">My Stock</span>
            </button>
            <button 
              onClick={() => {
                setCurrentView('routes');
                setActiveTab('routes');
              }}
              className={`flex flex-col items-center py-2 px-4 ${
                activeTab === 'routes' ? 'text-blue-600' : 'text-gray-400'
              }`}
            >
              <Route className="w-6 h-6" />
              <span className="text-xs mt-1">Routes</span>
            </button>
            <button 
              onClick={() => {
                setCurrentView('outlets');
                setActiveTab('outlets');
              }}
              className={`flex flex-col items-center py-2 px-4 ${
                activeTab === 'outlets' ? 'text-blue-600' : 'text-gray-400'
              }`}
            >
              <MapPin className="w-6 h-6" />
              <span className="text-xs mt-1">Outlets</span>
            </button>
          </div>
        </nav>
      </div>
    );
  }

  // Routes view: reuses the same map with sequenced polyline
  if (currentView === 'routes') {
    return (
      <div className="mobile-container bg-gray-50 min-h-screen">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  setCurrentView('dashboard');
                  setActiveTab('dashboard');
                }}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <Home className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center space-x-2">
                <Route className="w-6 h-6 text-blue-600" />
                <h1 className="text-lg font-semibold text-gray-800">Route Map</h1>
              </div>
            </div>
          </div>
        </header>

        <div className="p-4 pb-20">
          <div className="h-[calc(100vh-180px)] relative">
            {outlets.length === 0 ? (
              <div className="flex items-center justify-center h-full bg-gray-100 rounded-lg">
                <div className="text-center p-8">
                  <MapIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">No Outlets on Route</h3>
                  <p className="text-gray-600">Assign outlets to your route to view them here.</p>
                </div>
              </div>
            ) : (
              <div className="relative w-full h-full">
                <div 
                  key={`${currentView}-map`}
                  ref={mapContainerRef}
                  className="w-full h-full bg-gray-100 rounded-lg"
                />
                {!mapLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                    <div className="text-center">
                      <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                      <p className="text-gray-600">Loading map...</p>
                    </div>
                  </div>
                )}

                {/* Info Panel */}
                <div className="absolute top-4 left-4 right-4 bg-white rounded-lg shadow-lg p-3">
                  <div className="mb-2">
                    <h3 className="font-semibold text-gray-800">Sequenced Route</h3>
                    <p className="text-xs text-gray-600">Numbers indicate outlet visit order</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 max-w-md mx-auto">
          <div className="flex justify-around py-2">
            <button 
              onClick={() => {
                setCurrentView('dashboard');
                setActiveTab('dashboard');
              }}
              className={`flex flex-col items-center py-2 px-4 ${
                activeTab === 'dashboard' ? 'text-blue-600' : 'text-gray-400'
              }`}
            >
              <Home className="w-6 h-6" />
              <span className="text-xs mt-1">Home</span>
            </button>
            <button 
              onClick={() => {
                setCurrentView('inventory');
                setActiveTab('inventory');
              }}
              className={`flex flex-col items-center py-2 px-4 ${
                activeTab === 'inventory' ? 'text-blue-600' : 'text-gray-400'
              }`}
            >
              <Package className="w-6 h-6" />
              <span className="text-xs mt-1">My Stock</span>
            </button>
            <button 
              onClick={() => {
                setCurrentView('routes');
                setActiveTab('routes');
              }}
              className={`flex flex-col items-center py-2 px-4 ${
                activeTab === 'routes' ? 'text-blue-600' : 'text-gray-400'
              }`}
            >
              <Route className="w-6 h-6" />
              <span className="text-xs mt-1">Routes</span>
            </button>
            <button 
              onClick={() => {
                setCurrentView('outlets');
                setActiveTab('outlets');
              }}
              className={`flex flex-col items-center py-2 px-4 ${
                activeTab === 'outlets' ? 'text-blue-600' : 'text-gray-400'
              }`}
            >
              <MapPin className="w-6 h-6" />
              <span className="text-xs mt-1">Outlets</span>
            </button>
          </div>
        </nav>
      </div>
    );
  }

  // Default Salesman Dashboard
  return (
    <div className="mobile-container bg-gray-50 min-h-screen overflow-y-auto">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-800">SFA Dashboard</h1>
              <p className="text-sm text-gray-600">Welcome, {currentUser?.name}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <LogOut className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </header>

  {/* Stats Cards */}
      <div className="p-4 space-y-4 pb-20">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Today's Sales</p>
                <p className="text-2xl font-bold text-green-600">₹{stats.totalSales.toLocaleString()}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Target</p>
                <p className="text-2xl font-bold text-blue-600">₹{(12000).toLocaleString()}</p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Outlets Visited</p>
                <p className="text-2xl font-bold text-purple-600">{stats.outletsVisited}</p>
              </div>
              <Users className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Target Achievement</p>
                <p className="text-2xl font-bold text-orange-600">{stats.targetAchievement}%</p>
              </div>
              <Clock className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        </div>

  {/* Progress Bar */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Target Progress</span>
            <span className="text-sm text-gray-600">{stats.targetAchievement}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-green-400 to-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${stats.targetAchievement}%` }}
            ></div>
          </div>
        </div>

        {/* Vehicle Selection */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-800">Vehicle</h3>
            {vehiclesLoading && (
              <span className="text-xs text-gray-500">Loading…</span>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Select vehicle (warehouse-scoped, unassigned)</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={selectedVehicleId || ''}
                onChange={(e) => handleVehicleSelection(e.target.value || undefined)}
                disabled={['on_field','settling','settled'].includes(routeStatus?.status || '')}
              >
                <option value="">{assignedVehicleId ? 'Use assigned vehicle' : 'Select vehicle'}</option>
                {availableVehicles.map(v => (
                  <option key={v.vehicleId} value={v.vehicleId}>
                    {v.vehicleNumber} ({v.vehicleId})
                  </option>
                ))}
              </select>
            </div>
            <div className="text-sm text-gray-700">
              {assignedVehicleId && (
                <div>
                  Assigned: <span className="font-medium">{assignedVehicleId}</span>
                </div>
              )}
              <div>
                Selected: <span className="font-medium">{selectedVehicleId || assignedVehicleId || 'None'}</span>
              </div>
            </div>
          </div>
        </div>

  {/* Route Status Slider */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Route Status</h3>

          <div className="space-y-4">
            <div className="text-sm text-gray-700">Route: {(routeStatus as any)?.routeName || routeStatus?.routeId || 'Not planned'}</div>
            <div className="text-xs text-gray-600">Status: {routeStatus?.status || 'planning'}</div>
            {(() => {
              const stageKeys = ['planning','issuing','on_field','settling','settled'] as const;
              const stageLabels = ['Planning','Issuing','On Field','Settling','Settled'];
              const hasRoute = !!routeStatus;
              const currentKey = (routeStatus?.status as typeof stageKeys[number]) || 'planning';
              const stageIndex = hasRoute ? Math.max(0, stageKeys.indexOf(currentKey)) : -1;
              const progressPct = hasRoute ? (stageIndex / (stageKeys.length - 1)) * 100 : 0;

              const handleSegmentClick = async (targetKey: typeof stageKeys[number]) => {
                if (!hasRoute) return; // disabled when not planned yet
                // Allowed transitions: Issuing -> On Field, On Field <-> Settling
                if (currentKey === 'issuing' && targetKey === 'on_field') {
                  if (!confirmingOnField) await handleConfirmOnField();
                  return;
                }
                if (currentKey === 'on_field' && targetKey === 'settling') {
                  await handleToggleSettle();
                  return;
                }
                if (currentKey === 'settling' && targetKey === 'on_field') {
                  await handleToggleSettle();
                  return;
                }
              };

              return (
                <div className="relative w-full">
                  {/* Track */}
                  <div className="absolute top-1/2 left-0 right-0 h-2 bg-gray-200 rounded-full -translate-y-1/2"></div>
                  <div
                    className={`absolute top-1/2 left-0 h-2 rounded-full -translate-y-1/2 transition-all duration-500 ${hasRoute ? 'bg-blue-600' : 'bg-gray-300'}`}
                    style={{ width: `${progressPct}%` }}
                  ></div>

                  <div className="relative z-10 flex justify-between items-center">
                    {stageLabels.map((label, i) => {
                      const key = stageKeys[i];
                      const isCompleted = hasRoute && i < stageIndex;
                      const isActive = hasRoute && i === stageIndex;
                      const clickable = hasRoute && (
                        (currentKey === 'issuing' && key === 'on_field') ||
                        (currentKey === 'on_field' && key === 'settling') ||
                        (currentKey === 'settling' && key === 'on_field')
                      );

                      const baseClass = hasRoute
                        ? (isCompleted ? 'bg-blue-500 text-white border-blue-500' : isActive ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-500 border-gray-300')
                        : 'bg-white text-gray-400 border-gray-300';

                      return (
                        <div key={label} className="flex flex-col items-center group select-none">
                          <button
                            type="button"
                            onClick={() => clickable && handleSegmentClick(key)}
                            disabled={!clickable}
                            className={`flex items-center justify-center w-16 h-8 rounded-md transition-all duration-300 border text-sm font-semibold ${baseClass} ${clickable ? 'hover:ring-2 hover:ring-blue-300' : 'opacity-80'}`}
                            title={label}
                          >
                            {isCompleted ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                          </button>
                          <span className={`mt-2 text-xs font-medium ${hasRoute && (isActive || isCompleted) ? 'text-blue-600' : 'text-gray-500'}`}>
                            {label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {routeStatus?.status === 'settling' && (
              <div className="text-xs text-orange-700 bg-orange-50 px-2 py-1 rounded w-max">Pending Settlement</div>
            )}
            {routeStatus?.status === 'settled' && (
              <div className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded w-max">Settled</div>
            )}
          </div>
        </div>

        

      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 max-w-md mx-auto">
        <div className="flex justify-around py-2">
          <button 
            onClick={() => {
              setCurrentView('dashboard');
              setActiveTab('dashboard');
            }}
            className={`flex flex-col items-center py-2 px-4 ${
              activeTab === 'dashboard' ? 'text-blue-600' : 'text-gray-400'
            }`}
          >
            <Home className="w-6 h-6" />
            <span className="text-xs mt-1">Home</span>
          </button>
          <button 
            onClick={() => {
              setCurrentView('inventory');
              setActiveTab('inventory');
            }}
            className={`flex flex-col items-center py-2 px-4 ${
              activeTab === 'inventory' ? 'text-blue-600' : 'text-gray-400'
            }`}
          >
            <Package className="w-6 h-6" />
            <span className="text-xs mt-1">My Stock</span>
          </button>
          <button 
            onClick={() => {
              setCurrentView('routes');
              setActiveTab('routes');
            }}
            className={`flex flex-col items-center py-2 px-4 ${
              activeTab === 'routes' ? 'text-blue-600' : 'text-gray-400'
            }`}
          >
            <Route className="w-6 h-6" />
            <span className="text-xs mt-1">Routes</span>
          </button>
          <button 
            onClick={() => {
              setCurrentView('outlets');
              setActiveTab('outlets');
            }}
            className={`flex flex-col items-center py-2 px-4 ${
              activeTab === 'outlets' ? 'text-blue-600' : 'text-gray-400'
            }`}
          >
            <MapPin className="w-6 h-6" />
            <span className="text-xs mt-1">Outlets</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Dashboard;
